import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import qs from 'qs';
import { debounce, get, isEmpty } from 'lodash';
import { getLocation, replace } from 'connected-react-router';
import { PositionTitle } from '@src/views/CreateFunnel/PositionTitle';
import Collaborators from '@src/containers/Collaborators';
import { Sidebar } from '@src/components/Sidebar';
import { Container } from '@src/components/Container';
import { OPENING_ROUTE } from '@src/components/Preview/appRoutes';
import apolloClient from '@src/graphql/apollo';
import { EMPLOYMENT_TYPES } from '@src/constants';
import { MetricAction, MetricContext } from '@src/metrics/metrics.enum';
import { idify } from '@src/util/idify';
import { capitalize } from '@src/util/capitalize';
import { createLink } from '@src/util/createLink';
import { FUNNEL_EDIT_ROUTE } from '@src/Routes/links';
import {
  CurrentRecruiterForOpeningDocument,
  SaveOpeningDocument,
  OpeningVisibilityEnum,
  CurrentRecruiterForOpeningQuery,
  SenioritiesQueryQuery,
  GetSpecialitiesQuery,
} from '@src/graphql/generated';
import { ActiveTab } from '@src/components/Sidebar/enums/activeTab.enum';
import { ContainerType } from '@src/components/Container/enums/containerType.enum';
import { trackEvent } from '@src/metrics';
import PartTimeHours from './PartTimeHours';
import EmploymentType from './EmploymentType';
import { View } from './View';
import { SetField } from './interface/setField.interface';
import { State } from './interfaces/state.interface';
import { SkillData } from './interfaces/skill.interface';
import { CreateOpeningProps } from './props';

@connect(state => ({ location: getLocation(state) }), { replace })
export default class CreateOpening extends PureComponent<CreateOpeningProps, State> {
  state: State = {
    errors: undefined,
    opening: this.props.opening,
    openingId: this.props.openingId,
    recruiterRights: undefined,
    saving: false,
    updatedAt: undefined,
  };

  previousSaveOpeningController = undefined;
  _isMounted = false;

  async componentDidMount() {
    this._isMounted = true;
    const { opening, location } = this.props;

    const {
      data: {
        currentRecruiter: { rights: recruiterRights },
      },
    } = await apolloClient.query<CurrentRecruiterForOpeningQuery>({
      fetchPolicy: 'no-cache',
      query: CurrentRecruiterForOpeningDocument,
    });

    this.setState({ recruiterRights });

    const { employmentType } = qs.parse(location.search, { ignoreQueryPrefix: true });

    if (employmentType) {
      this.setEmploymentType(employmentType);
    }

    if (opening?.id) {
      this.redirectToEdit(opening.id);
    }
  }

  componentDidUpdate(prevProps: CreateOpeningProps) {
    if (isEmpty(prevProps.opening) && !isEmpty(this.props.opening)) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({ opening: this.props.opening });
    }
  }

  redirectToEdit = (openingId: string): void => {
    const { replace } = this.props;
    replace({ pathname: createLink(FUNNEL_EDIT_ROUTE, { params: { openingId } }) });
  };

  componentWillUnmount() {
    this._isMounted = false;
  }

  saveOpeningDebounce = debounce(async (openingInput: Record<string, unknown>) => {
    const { opening } = this.state;
    const openingId = opening && opening.id;
    const prevOpening = { ...this.state.opening };
    const hasId = !!openingId;
    const { recruiter } = this.props;

    if (openingInput?.relocationBenefits) {
      openingInput.relocationBenefits = openingInput.relocationBenefits.map(benefit => benefit.id);
    }

    if (openingInput?.positionLocations) {
      openingInput.positionLocations = openingInput.positionLocations.map(positionLocation => positionLocation.placeId);
    }

    if (openingInput?.targetLocations) {
      openingInput.targetLocations = openingInput.targetLocations.map(targetLocation => targetLocation.placeId);
    }

    try {
      const {
        data: { saveOpening: opening },
      } = await apolloClient.mutate({
        mutation: SaveOpeningDocument,
        variables: {
          openingId,
          openingInput,
        },
      });

      if (!prevOpening.recruiterId) {
        prevOpening.recruiterId = recruiter?.id;
      }

      let newState = {
        opening: { ...prevOpening, ...opening },
        saving: false,
      };

      let callback;

      if (!hasId && opening.id) {
        callback = () => this.redirectToEdit(opening.id);
        newState = { opening: { ...prevOpening, ...opening }, saving: false };
        this.setState(newState, callback);
      }

      this.setState({ saving: false });
    } catch (error) {
      this.setState(prevState => ({
        ...prevState,
        errors: {
          ...prevState?.errors,
          ...openingInput,
        },
      }));

      if (error?.code !== 0) {
        throw Error(error?.message);
      }
    }
  }, 500);

  abortLatest = (): void => {
    this.previousSaveOpeningController && this.previousSaveOpeningController.abort();
  };

  setSpeciality = (speciality: GetSpecialitiesQuery['specialities'][0]): void => {
    const { opening } = this.state;

    if (opening?.speciality && speciality?.id === opening.speciality.id) {
      return;
    }

    const newOpeningInput = {
      seniorities: [],
      skills: [],
      speciality: speciality?.id,
    };

    const newOpeningState = {
      seniorities: [],
      skills: [],
      speciality: speciality,
    };

    this.setState(
      ({ opening }) => ({
        opening: {
          ...opening,
          ...newOpeningState,
        },
        saving: true,
      }),
      () => this.saveOpeningDebounce(newOpeningInput),
    );

    trackEvent({
      context: MetricContext.CREATE_OPENING,
      interaction: MetricAction.SET_SPECIALTY,
      payload: {
        'Opening Id': opening ? idify(opening) : undefined,
        'Speciality Id': speciality ? idify(speciality) : undefined,
        'Speciality Name': speciality?.name,
      },
    });
  };

  // For target/position locations update it is not necessary to update opening state first.
  setField: SetField = (payload, shouldTriggerStateUpdateFirst = true) => {
    if (shouldTriggerStateUpdateFirst) {
      this.setState(
        ({ opening }) => ({
          opening: {
            ...opening,
            ...payload,
          },
          saving: true,
        }),
        () => this.saveOpeningDebounce(payload),
      );
    } else {
      this.saveOpeningDebounce(payload);
    }
  };

  setEmploymentType = (employmentType: string): void => {
    const { opening } = this.state;

    if (opening?.employmentType && employmentType === opening.employmentType) {
      return;
    }

    const newOpeningInput = {
      employmentType: employmentType,
      salaryRange: null,
      workload: { hours: 40, period: 'WEEK' },
    };

    this.setState(
      ({ opening }: State): State => ({
        opening: {
          ...opening,
          ...newOpeningInput,
        },
        saving: true,
      }),
      () => this.saveOpeningDebounce(newOpeningInput),
    );

    trackEvent({
      context: MetricContext.CREATE_OPENING,
      interaction: MetricAction.SET_EMPLOYMENT_TYPE,
      payload: { 'Employment Type': capitalize(employmentType) },
    });
  };

  setOpeningVisibility = (visibility: OpeningVisibilityEnum): void => {
    const { opening } = this.state;

    if (opening?.visibility && visibility === opening?.visibility) {
      return;
    }

    const newOpeningInput = {
      visibility: visibility,
    };

    this.setState(
      ({ opening }) => ({
        opening: {
          ...opening,
          visibility: visibility,
        },
        saving: true,
      }),
      () => this.saveOpeningDebounce(newOpeningInput),
    );

    trackEvent({
      context: MetricContext.CREATE_OPENING,
      interaction: MetricAction.SET_OPENING_VISIBILITY,
      payload: { 'Opening Visibility': capitalize(visibility) },
    });
  };

  setSeniorities = (seniorities: SenioritiesQueryQuery['seniorities']): void => {
    const { opening } = this.state;

    const newOpeningInput = {
      seniorities: seniorities.map(seniority => seniority?.id),
    };

    this.setState(
      ({ opening }) => ({
        opening: {
          ...opening,
          seniorities,
        },
        saving: true,
      }),
      () => this.saveOpeningDebounce(newOpeningInput),
    );

    trackEvent({
      context: MetricContext.CREATE_OPENING,
      interaction: MetricAction.SET_SENIORITY,
      payload: {
        'Opening Id': opening?.id,
        'Seniority Ids': seniorities.map(seniority => seniority?.id),
        'Seniority Names': seniorities.map(seniority => seniority?.name),
      },
    });
  };

  setSkills = (skills: SkillData[]): void => {
    const { opening } = this.state;

    const openingSkillsInput = !isEmpty(skills)
      ? skills.map(({ _id, isMandatory }) => ({
          isMandatory: isMandatory,
          skill: _id,
        }))
      : [];

    this.setState(
      ({ opening }) => {
        if (!skills.length) {
          return {
            opening: {
              ...opening,
              skills,
            },
            saving: true,
          };
        }

        return {
          opening: {
            ...opening,
            skills,
          },
          saving: true,
        };
      },
      () =>
        this.saveOpeningDebounce({
          skills: openingSkillsInput,
        }),
    );

    trackEvent({
      context: MetricContext.CREATE_OPENING,
      interaction: MetricAction.SET_SKILLS,
      payload: {
        'Opening Id': opening?.id,
        'Skill Ids': skills.map(({ skill }) => (skill ? idify(skill) : undefined)),
        'Skill Names': skills.map(({ skill: { name } }) => name),
      },
    });
  };

  render() {
    const { recruiter, benefits, company } = this.props;

    const { opening, recruiterRights, updatedAt, saving } = this.state;

    const { employmentType, workload } = opening;

    const openingId = opening?.id;
    const companyProfile = company?.profile;

    return (
      <div className="page create-opening funnels">
        <div className="page-layout">
          <Sidebar
            benefits={benefits}
            companyProfile={companyProfile}
            defaultTab={ActiveTab.STATS}
            isSaving={saving}
            opening={opening}
            previewUrl={OPENING_ROUTE}
          />

          <Container type={ContainerType.BORDERED}>
            <div className="page-section">
              <EmploymentType
                employmentType={employmentType}
                setEmploymentType={value => this.setEmploymentType(value)}
              />
            </div>

            {employmentType === EMPLOYMENT_TYPES.PART_TIME.key ? (
              <div className="page-section">
                <PartTimeHours
                  setWorkload={value => {
                    this.setField({ workload: value });
                    trackEvent({
                      context: MetricContext.CREATE_OPENING,
                      interaction: MetricAction.SET_WORKLOAD,
                      payload: {
                        Hours: value.hours,
                        Period: capitalize(value.period),
                      },
                    });
                  }}
                  workload={workload}
                />
              </div>
            ) : null}

            {employmentType &&
            openingId &&
            (employmentType !== EMPLOYMENT_TYPES.PART_TIME.key || (workload && workload.hours < 40)) ? (
              <React.Fragment>
                <div className="page-section">
                  <PositionTitle
                    initTitle={opening.title || undefined}
                    openingId={openingId}
                    setTitle={value => this.setField({ title: value })}
                  />
                </div>

                {opening.title && get(recruiterRights, 'inviteCollaborator') && !get(recruiter, 'isSiteAdmin') && (
                  <div className="page-section">
                    <Collaborators metricsContext={MetricContext.CREATE_OPENING} opening={opening} />
                  </div>
                )}

                {opening.title && (
                  <View
                    opening={opening}
                    saving={saving}
                    setField={this.setField}
                    setSeniorities={this.setSeniorities}
                    setSkills={this.setSkills}
                    setSpeciality={this.setSpeciality}
                    updatedAt={updatedAt}
                  />
                )}
              </React.Fragment>
            ) : null}
          </Container>
        </div>
      </div>
    );
  }
}
